import * as React from 'react'
import dynamic from 'next/dynamic'

import { Tooltip } from '@thg-commerce/gravity-elements/Tooltip'

import { SwatchShape, SwatchSize, ColourChoice } from '../Swatch'
import {
  SwatchStyledProps,
  SwatchElementContainer,
  SwatchElementButton,
  OuterSwatchElement,
  InnerSwatchElement,
  SwatchDisableWrapper,
} from '../styles'

const SwatchDisableCircle = dynamic(
  () =>
    import('@thg-commerce/gravity-icons/src/components/SwatchDisableCircle'),
)
const SwatchDisableSquare = dynamic(
  () =>
    import('@thg-commerce/gravity-icons/src/components/SwatchDisableSquare'),
)

export interface SwatchElementProps {
  choice: ColourChoice
  onClick?: React.MouseEventHandler<HTMLButtonElement>
  selected: boolean
  shape: SwatchShape
  size?: SwatchSize
  i18nText: {
    unavailableText: string
    closeButtonText: string
  }
}

export const SwatchElement = (props: SwatchElementProps) => {
  const [tooltipOpen, setTooltipOpen] = React.useState<boolean>(false)

  const swatchStyledProps: SwatchStyledProps = {
    backgroundColor: props.choice.colour,
    selected: props.selected,
    swatchShape: props.shape,
    swatchSize: props.size || SwatchSize.Large,
  }

  const swatchElement = (
    <SwatchElementButton
      type={props.onClick && 'button'}
      as={!props.onClick && props.choice.href ? 'a' : 'button'}
      href={props.choice.href}
      onClick={props.onClick}
      aria-label={
        props.choice.disabled
          ? `${props.choice.title} ${props.i18nText.unavailableText}`
          : props.choice.title
      }
      data-testid={`${props.choice.title.replace(/\s+/g, '')}-${
        props.shape === SwatchShape.Circle
          ? SwatchShape.Circle
          : SwatchShape.Square
      }-swatch${props.selected ? '-selected' : ''}`}
      {...(props.choice.disabled &&
        !props.choice.href && { 'aria-expanded': tooltipOpen })}
      {...swatchStyledProps}
    >
      <OuterSwatchElement
        disabled={props.choice.disabled}
        {...swatchStyledProps}
      >
        {props.choice.disabled && (
          <SwatchDisableWrapper
            borderWidth={props.size === SwatchSize.Small ? '2px' : '4px'}
          >
            {props.shape === SwatchShape.Circle ? (
              <SwatchDisableCircle />
            ) : (
              <SwatchDisableSquare />
            )}
          </SwatchDisableWrapper>
        )}

        <InnerSwatchElement
          borderWidth={props.size === SwatchSize.Small ? '2px' : '4px'}
          elementSize={props.size === SwatchSize.Small ? '24px' : '38px'}
          disabled={props.choice.disabled}
          {...swatchStyledProps}
        />
      </OuterSwatchElement>
    </SwatchElementButton>
  )

  return (
    <SwatchElementContainer swatchSize={props.size || SwatchSize.Large}>
      {props.choice.disabled && !props.choice.href ? (
        <Tooltip
          onChange={(open: boolean) => setTooltipOpen(open)}
          triggerContent={<div>{swatchElement}</div>}
          content={
            <React.Fragment>
              {`${props.choice.key} - ${props.i18nText.unavailableText}`}
            </React.Fragment>
          }
          i18nCloseButtonText={props.i18nText.closeButtonText}
        />
      ) : (
        swatchElement
      )}
    </SwatchElementContainer>
  )
}
